import { Box, List, ListItemButton, ListItemText, Paper, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import type { RegistryFundOpportunityAllocations } from '../../api/registryEntityTypes';
import { formatAmount } from '../../entities/entityFormat';
import { useEntitySource } from '../../entities/useEntitySource';
import { useSourceQuery } from '../../entities/useSourceQuery';
import { AboutThisPage, PageHeader, SectionCard } from '../../ui/primitives';
import AllocationsTable from '../entities/AllocationsTable';
import SourceNotice, { QueryStatus } from '../entities/SourceNotice';

const FUND_STATUS_LABELS: Record<string, string> = {
  NEW: 'New',
  APPROVED: 'Approved',
  EOI: 'Book build',
  APPLICATION_OPEN: 'Applications open',
  UNDER_MANAGEMENT: 'Under management',
  COMPLETE: 'Complete',
};

function FundAllocations({ fund }: { fund: RegistryFundOpportunityAllocations }) {
  const source = useEntitySource();
  const load = useCallback(
    () => source.listFundOpportunityAllocations(fund.externalId),
    [source, fund.externalId],
  );
  const query = useSourceQuery(load);
  return (
    <SectionCard
      title={fund.name}
      subtitle={`${fund.allocationCount} allocations to ${fund.entityCount} entities · ${formatAmount(fund.totalAllocated)}`}
      noPadding
    >
      <QueryStatus query={query}>
        {(allocations) => <AllocationsTable allocations={allocations} groupBy="entity" />}
      </QueryStatus>
    </SectionCard>
  );
}

function FundPicker({ funds }: { funds: RegistryFundOpportunityAllocations[] }) {
  const [selectedId, setSelectedId] = useState(funds[0]?.externalId);
  const selected = funds.find((fund) => fund.externalId === selectedId);
  if (!selected)
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No fund opportunity has allocations yet.</Typography>
      </Paper>
    );
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: '280px 1fr' } }}>
      <Paper variant="outlined">
        <List dense aria-label="Fund opportunities">
          {funds.map((fund) => (
            <ListItemButton
              key={fund.externalId}
              selected={fund.externalId === selected.externalId}
              onClick={() => setSelectedId(fund.externalId)}
            >
              <ListItemText
                primary={fund.name}
                secondary={`${FUND_STATUS_LABELS[fund.status] ?? fund.status} · ${formatAmount(fund.totalAllocated)}`}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>
      <FundAllocations key={selected.externalId} fund={selected} />
    </Box>
  );
}

/** Allocations by fund opportunity, read from Fides. Applications start from these. */
export default function AllocationsPage() {
  const source = useEntitySource();
  const load = useCallback(() => source.listFundOpportunities(), [source]);
  const query = useSourceQuery(load);
  return (
    <>
      <PageHeader
        title="Allocations"
        subtitle="Investor allocations to investment entities, by fund opportunity. Only entities with an allocation are invited to apply."
      />
      <AboutThisPage>
        <p>
          Allocations are made in the admin portal and read here. Starting an application from an
          allocation creates a sample application under a sample offer until offers and applications
          are built.
        </p>
      </AboutThisPage>
      <SourceNotice sample={source.sample} />
      <QueryStatus query={query}>{(funds) => <FundPicker funds={funds} />}</QueryStatus>
    </>
  );
}
